import Link from "next/link";
import { EmptyValue } from "@/components/ui/record-table/empty-value";
import { Image } from "@/components/ui/image";
import { ProductCardPlaceholder } from "@/components/features/listings/product-card-placeholder";
import { formatCentsSmart } from "@/lib/formatting/currency";
import { getProductTypeBySlug } from "@/lib/generated/product-types";
import { CHECK_AVAILABILITY_LABEL } from "@shared/products/listing-copy";
import type { BrowseListing } from "@/components/listing/browse/marketplace-listing";

export function ProductTypeCell({ slug }: { slug: string | undefined }) {
  const productType = slug ? getProductTypeBySlug(slug) : null;
  const label = productType?.pluralDisplayName || slug;
  if (!label) return <EmptyValue />;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-brand-tint text-xs text-brand-strong font-medium">
      {productType?.iconUrl && (
        <Image
          src={productType.iconUrl}
          alt=""
          width={14}
          height={14}
          className="w-3.5 h-3.5 object-contain opacity-70"
        />
      )}
      {label}
    </span>
  );
}

export function ProductImageCell({ product }: { product: BrowseListing }) {
  return (
    <div className="size-9 bg-muted rounded-md overflow-hidden flex-shrink-0 border border-border/50">
      {product.primaryImageUrl ? (
        <Image
          src={product.primaryImageUrl}
          alt={product.title}
          width={36}
          height={36}
          sizes="36px"
          className="w-full h-full object-cover"
        />
      ) : (
        <ProductCardPlaceholder
          productTypeSlug={product.productTypeSlug}
          iconClassName="size-5"
        />
      )}
    </div>
  );
}

export function LocationCell({
  city,
  state,
}: {
  city?: string | null;
  state?: string | null;
}) {
  const place = city && state ? `${city}, ${state}` : city || state;
  if (!place) return <EmptyValue />;
  return <span className="block truncate text-foreground">{place}</span>;
}

export function PriceCell({
  cents,
  minimumOrder = null,
}: {
  cents: number | null | undefined;
  minimumOrder?: string | null;
}) {
  const label =
    typeof cents !== "number"
      ? "Price on request"
      : cents === 0
        ? "Free"
        : formatCentsSmart(cents);

  return (
    <span className="flex flex-col items-end">
      <span className="text-sm font-semibold text-foreground tabular-nums">
        {label}
      </span>
      {minimumOrder && typeof cents === "number" && cents > 0 ? (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {minimumOrder}
        </span>
      ) : null}
    </span>
  );
}

export function AvailabilityCell({
  product,
  quoteHref,
  onSelect,
}: {
  product: BrowseListing;
  quoteHref: string;
  onSelect?: (product: BrowseListing) => void;
}) {
  const quantity = product.availableToSell ?? 0;
  if (product.intent === "recycle" && quantity > 0) {
    return (
      <span className="whitespace-nowrap tabular-nums text-foreground">
        {quantity.toLocaleString("en-US")} available
      </span>
    );
  }
  if (product.checkoutEnabled) {
    return <span className="whitespace-nowrap text-foreground">In stock</span>;
  }
  return (
    <Link
      prefetch={false}
      href={quoteHref}
      onClick={() => onSelect?.(product)}
      aria-label={`${CHECK_AVAILABILITY_LABEL} — ${product.title || "this listing"}`}
      className="whitespace-nowrap font-medium text-link underline underline-offset-4 hover:text-link-hover"
    >
      {CHECK_AVAILABILITY_LABEL}
    </Link>
  );
}
